import type { AgenticSnapshot } from "../api/agenticClient";

const AGENTIC_SELECTED_ACCOUNT_KEY = "sfe-agentic-selected-account";

function parseAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Match Agentic `readSnapshotData`: MCP payloads nest `{ data: { …fields } }`. */
function unwrapSnapshotToolData(tool: { ok?: boolean; data?: unknown } | undefined): unknown {
  if (!tool || tool.ok === false) return null;
  const data = tool.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const nested = (data as { data?: unknown }).data;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) return nested;
  return data;
}

function buyingPowerObject(portfolio: Record<string, unknown>): Record<string, unknown> | null {
  return portfolio.buying_power &&
    typeof portfolio.buying_power === "object" &&
    !Array.isArray(portfolio.buying_power)
    ? (portfolio.buying_power as Record<string, unknown>)
    : null;
}

function portfolioBuyingPowerFields(portfolio: Record<string, unknown>): {
  cash: number | null;
  buyingPower: number | null;
  unleveraged: number | null;
} {
  const buyingPowerObj = buyingPowerObject(portfolio);
  return {
    cash: parseAmount(portfolio.cash),
    buyingPower: parseAmount(buyingPowerObj?.buying_power),
    unleveraged: parseAmount(buyingPowerObj?.unleveraged_buying_power),
  };
}

/**
 * What you can actually spend (cash-secured puts, stock, withdrawals).
 * Robinhood’s headline buying power is often 2× overnight / PDT leverage.
 */
export function cashEquivalentFromPortfolio(portfolioRaw: unknown): number | null {
  if (!portfolioRaw || typeof portfolioRaw !== "object" || Array.isArray(portfolioRaw)) {
    return null;
  }
  const { cash, buyingPower, unleveraged } = portfolioBuyingPowerFields(
    portfolioRaw as Record<string, unknown>,
  );
  if (unleveraged != null && unleveraged > 0) return unleveraged;
  if (cash != null && cash > 0) return cash;
  if (unleveraged != null) return 0;
  if (cash != null && cash <= 0) return 0;
  if (buyingPower != null) return Math.max(0, buyingPower);
  return null;
}

/**
 * Spendable capacity for cash-secured puts.
 * Margin accounts often report negative cash — do not fall back to leveraged BP.
 */
export function availableCashFromPortfolio(portfolioRaw: unknown): number {
  const amount = cashEquivalentFromPortfolio(portfolioRaw);
  return amount != null && amount > 0 ? amount : 0;
}

/** Robinhood buying power for the active account (margin-inclusive, often 2× cash). */
export function buyingPowerFromPortfolio(portfolioRaw: unknown): number | null {
  if (!portfolioRaw || typeof portfolioRaw !== "object" || Array.isArray(portfolioRaw)) return null;
  const { cash, buyingPower, unleveraged } = portfolioBuyingPowerFields(
    portfolioRaw as Record<string, unknown>,
  );
  if (buyingPower != null) return buyingPower;
  if (unleveraged != null) return unleveraged;
  return cash;
}

function resolveAccountNumber(snapshot: AgenticSnapshot | null): string | null {
  const accounts = snapshot?.accounts ?? [];
  if (!accounts.length) return null;
  try {
    const stored = localStorage.getItem(AGENTIC_SELECTED_ACCOUNT_KEY)?.trim();
    if (stored && accounts.some((a) => a.accountNumber === stored)) return stored;
  } catch {
    /* ignore */
  }
  return accounts.find((a) => a.isDefault)?.accountNumber ?? accounts[0].accountNumber;
}

export function activeAccountNumberFromSnapshot(snapshot: AgenticSnapshot | null): string | null {
  return resolveAccountNumber(snapshot);
}

export function activeAccountPortfolioFromSnapshot(snapshot: AgenticSnapshot | null): unknown {
  if (!snapshot?.byAccount) return null;
  const account = resolveAccountNumber(snapshot);
  if (!account) {
    const first = Object.values(snapshot.byAccount)[0];
    return unwrapSnapshotToolData(first?.portfolio);
  }
  return unwrapSnapshotToolData(snapshot.byAccount[account]?.portfolio);
}

export function availableCashFromActiveAccount(snapshot: AgenticSnapshot | null): number {
  return availableCashFromPortfolio(activeAccountPortfolioFromSnapshot(snapshot));
}

export function buyingPowerFromActiveAccount(snapshot: AgenticSnapshot | null): number | null {
  return buyingPowerFromPortfolio(activeAccountPortfolioFromSnapshot(snapshot));
}

export function cashEquivalentFromActiveAccount(snapshot: AgenticSnapshot | null): number | null {
  return cashEquivalentFromPortfolio(activeAccountPortfolioFromSnapshot(snapshot));
}

export function buyingPowerForAccountNumber(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null | undefined,
): number | null {
  const ref = String(accountNumber || "").trim();
  if (!snapshot?.byAccount || !ref) return null;
  return buyingPowerFromPortfolio(unwrapSnapshotToolData(snapshot.byAccount[ref]?.portfolio));
}

export function cashEquivalentForAccountNumber(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null | undefined,
): number | null {
  const ref = String(accountNumber || "").trim();
  if (!snapshot?.byAccount || !ref) return null;
  return cashEquivalentFromPortfolio(unwrapSnapshotToolData(snapshot.byAccount[ref]?.portfolio));
}

export const AGENTIC_ACCOUNT_CHANGED_EVENT = "sfe:agentic-account-changed";

export function notifyAgenticAccountChanged(): void {
  window.dispatchEvent(new CustomEvent(AGENTIC_ACCOUNT_CHANGED_EVENT));
}

export function activeAccountLabelFromSnapshot(snapshot: AgenticSnapshot | null): string | null {
  const accountNumber = resolveAccountNumber(snapshot);
  if (!accountNumber) return null;
  const account = snapshot?.accounts?.find((row) => row.accountNumber === accountNumber);
  const name = account?.name?.trim();
  if (name) return name;
  return accountNumber.length > 4 ? `···${accountNumber.slice(-4)}` : accountNumber;
}

export function portfolioFromSnapshot(snapshot: AgenticSnapshot | null): unknown {
  if (!snapshot?.byAccount) return null;
  const preferred = resolveAccountNumber(snapshot);
  const accountOrder = [
    preferred,
    ...snapshot.accounts.map((a) => a.accountNumber).filter((id) => id && id !== preferred),
    ...Object.keys(snapshot.byAccount),
  ].filter((id): id is string => Boolean(id));

  let best: { cash: number; portfolio: unknown } | null = null;
  const seen = new Set<string>();
  for (const account of accountOrder) {
    if (seen.has(account)) continue;
    seen.add(account);
    const portfolio = unwrapSnapshotToolData(snapshot.byAccount[account]?.portfolio);
    const cash = availableCashFromPortfolio(portfolio);
    if (cash <= 0) continue;
    if (account === preferred) return portfolio;
    if (!best || cash > best.cash) best = { cash, portfolio };
  }
  return (
    best?.portfolio ??
    unwrapSnapshotToolData(
      preferred
        ? snapshot.byAccount[preferred]?.portfolio
        : Object.values(snapshot.byAccount)[0]?.portfolio,
    )
  );
}

export async function loadAvailableCashFromAgenticSnapshot(
  fetchSnapshot: () => Promise<{ snapshot: AgenticSnapshot | null }>,
): Promise<number> {
  try {
    const { snapshot } = await fetchSnapshot();
    return availableCashFromPortfolio(portfolioFromSnapshot(snapshot));
  } catch {
    return 0;
  }
}
